import LinkHeader from "http-link-header"
import Cookies from "js-cookie"
import {
  CurrentOrg,
  CurrentOrgId,
  CurrentUser,
  Datasets,
  DatasetSchemas,
  HashedSchemas,
  Schemas,
  JobStatus,
  UploadBatchStatus,
  IsUploading,
  UploadStatus,
  UploadProgress,
} from "./store"
import env from "./env"
import process from "process"
import { throttle } from "lodash"
import { addAlert, AlertType } from "./alerts"
import { unrollError } from "./util"

// TODO(shakefu): Hoist these to a constants.js?
export const AUTH_EMAIL = "auth.username"
export const AUTH_ORGANIZATION_ID = "auth.organization_id"

// TODO(shakefu): Make this work more better
// This environment variable has NO EFFECT in local development, it's only used
// by the Docker entrypoint script within the built container
const defaultApi =
  process.env.BLINDINSIGHT_PROXY_URL || env.BLINDINSIGHT_PROXY_URL

// The default amoutn of items to return unless otherwise specified by a caller.
const DEFAULT_LIMIT = 50
const DEFAULT_OFFSET = 0

function getIdFromUrl(url) {
  const parts = url.replace(/\/+$/, "").split("/")
  return parts.pop()
}

function updateDatasetSchemas(data) {
  // Create a mapping of datasetId to [schemaIds]
  let datasetMap = {}
  for (let schema of data) {
    if (!schema.dataset) {
      continue
    }
    let datasetId = getIdFromUrl(schema.dataset)
    if (!datasetMap[datasetId]) {
      datasetMap[datasetId] = []
    }
    datasetMap[datasetId].push(schema.id)
  }

  // Update the datasetSchemas state
  DatasetSchemas.updateValue((datasetSchemas) => {
    for (let datasetId in datasetMap) {
      if (!datasetSchemas[datasetId]) {
        datasetSchemas[datasetId] = datasetMap[datasetId]
        continue
      }
      datasetSchemas[datasetId] = [
        ...new Set([...datasetSchemas[datasetId], ...datasetMap[datasetId]]),
      ]
    }
  })
}

export class Api {
  constructor(options) {
    this.api = options?.api || defaultApi
    for (let key in options) {
      if (!this[key]) {
        throw new Error(`${key} is not a valid option`)
      }
      if (!options[key]) {
        delete options[key]
      }
      this[key] = options[key]
    }
  }

  getAuthUsername() {
    return sessionStorage.getItem(AUTH_EMAIL)
  }

  makeApiUrl(path) {
    return `${this.api}${path}`
  }

  async fetch(path, options = {}) {
    let uri = this.makeApiUrl(path)

    // Set the default headers and any provided by caller.
    if (!options?.headers) {
      options.headers = {}
    }

    // If no Content-Type is provided, default to JSON.
    if (!options.headers["Content-Type"]) {
      options.headers["Content-Type"] = "application/json"
    }

    // Pass along the CSRF token if it exists.
    const csrftoken = Cookies.get("csrftoken") || null
    if (csrftoken) {
      options.headers["X-CSRFToken"] = csrftoken
    }

    // Handle query parameters by merging existing params and ovewriting
    if (options.params) {
      let url = new URL(uri)
      let params = {}
      for (const [key, value] of url.searchParams) {
        params[key] = value
      }
      for (const [key, value] of Object.entries(options.params)) {
        params[key] = value
      }
      url.search = new URLSearchParams(params).toString()
      uri = url.toString()
    }

    // Always pass through cookies to the API.
    options.credentials = "include"

    const resp = await fetch(uri, options)

    if (resp.status === 402) {
      const subscriptionStatusCall = uri.includes(
        "/api/stripe/subscription-status",
      )
      const csrfCall = uri.includes("/api/csrf/")
      if (
        typeof window !== "undefined" &&
        !subscriptionStatusCall &&
        !csrfCall &&
        !window.location.pathname.startsWith("/choose-plan") &&
        !window.location.pathname.startsWith("/organizations/create")
      ) {
        window.location.assign("/choose-plan?reason=inactive")
      }
      throw resp
    }

    if (resp && !resp.ok) {
      if (resp.status > 399) {
        console.error("Api call failed", uri, resp.status, resp.statusText)
      }
      throw resp
    } else if (!resp) {
      throw new Response("Unknown error", { status: 500 })
    }

    return resp
  }

  async call(path, options = {}, callback) {
    let resp
    try {
      resp = await this.fetch(path, options)
    } catch (error) {
      if (error.status === 413) {
        const errorData = await error.json().catch(() => ({}))
        const errorMessage =
          errorData.error ||
          errorData.detail ||
          "Request exceeds system limits. Please reduce the number of string filter combinations or narrow your query."
        throw new Response(errorMessage, { status: 413 })
      }
      throw error
    }
    const data = await resp.json()
    const linkHeader = resp.headers.get("Link")
    const countHeader = resp.headers.get("X-Total-Count")

    /// Add data.links if set
    if (linkHeader) {
      const links = LinkHeader.parse(linkHeader)
      Object.defineProperty(data, "links", {
        value: {
          first: links.rel("first")[0]?.uri,
          next: links.rel("next")[0]?.uri,
          last: links.rel("last")[0]?.uri,
        },
        writable: false,
        enumerable: false,
      })
    }

    /// Add data.count from X-Total-Count when present and valid
    if (countHeader != null && countHeader !== "") {
      const total = parseInt(countHeader, 10)
      if (!Number.isNaN(total) && total >= 0) {
        Object.defineProperty(data, "count", {
          value: total,
          writable: false,
          enumerable: true,
        })
      }
    }

    /// Expose X-Query header so the UI can fire a follow-up count-only GET
    const queryHeader = resp.headers.get("X-Query")
    if (queryHeader) {
      Object.defineProperty(data, "xQuery", {
        value: queryHeader,
        writable: false,
        enumerable: false,
      })
    }

    if (callback) callback(data)
    return data
  }

  setOrFindOrganization(organizations) {
    if (!organizations?.length) return
    let currentOrgId = sessionStorage.getItem(AUTH_ORGANIZATION_ID)
    if (!currentOrgId) {
      currentOrgId = organizations[0].id
      sessionStorage.setItem(AUTH_ORGANIZATION_ID, currentOrgId)
    }

    let currentOrg = organizations.find((org) => org.id === currentOrgId)
    if (!currentOrg) {
      currentOrgId = organizations[0].id
      sessionStorage.setItem(AUTH_ORGANIZATION_ID, currentOrgId)
      currentOrg = organizations[0]
    }

    // Update state
    CurrentOrg.setValue(currentOrg)
    CurrentOrgId.setValue(currentOrgId)
  }

  clearAuth() {
    sessionStorage.removeItem(AUTH_EMAIL)
    sessionStorage.removeItem(AUTH_ORGANIZATION_ID)
  }

  // Manually fetch a CSRF token from API server.
  async getCsrfToken() {
    const resp = await this.fetch("/api/csrf/")
    const payload = await resp.json()
    return payload.csrfToken
  }

  async googleLogin(credential, clientId) {
    try {
      const csrfToken = await this.getCsrfToken()

      Cookies.set("csrftoken", csrfToken, {
        path: "/",
        domain: ".blindinsight.io",
        secure: true,
        sameSite: "none",
        expires: 365,
      })

      const options = {
        method: "POST",
        body: JSON.stringify({
          provider: "google",
          process: "login",
          token: {
            client_id: clientId,
            id_token: credential,
          },
        }),
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      }

      await this.fetch("/api/allauth/browser/v1/auth/provider/token", options)
      window.location.href = "/"
    } catch (error) {
      console.error("Google sign-in failed", error)
      const message = await unrollError(error)
      addAlert(AlertType.ERROR, "Google sign-in", message)
    }
  }

  //#region Accounts
  async accountsLogin(email, password) {
    const options = {
      method: "POST",
      body: JSON.stringify({ login: email, password: password }),
    }
    return await this.fetch("/api/accounts/login/", options)
  }

  async accountsLogout() {
    const options = {
      method: "POST",
    }
    const resp = await this.fetch("/api/accounts/logout/", options)
    this.clearAuth()
    return resp
  }

  async accountsRegister(email, password, fullName, organizationName) {
    const nameParts = fullName.split(" ")
    let firstName = nameParts[0]
    let lastName = nameParts.slice(1).join(" ")
    const options = {
      method: "POST",
      body: JSON.stringify({
        email: email,
        first_name: firstName,
        last_name: lastName,
        organization_name: organizationName,
        password: password,
        password_confirm: password,
      }),
    }
    return await this.call("/api/accounts/register/", options)
  }

  async accountsVerifyRegistration(user_id, timestamp, signature) {
    const options = {
      method: "POST",
      body: JSON.stringify({ user_id, timestamp, signature }),
    }
    return await this.call("/api/accounts/verify-registration/", options)
  }

  async accountsChangePassword(oldPassword, password, passwordConfirm) {
    const options = {
      method: "POST",
      body: JSON.stringify({
        old_password: oldPassword,
        password: password,
        password_confirm: passwordConfirm,
      }),
    }
    return await this.call("/api/accounts/change-password/", options)
  }

  async accountsSendPasswordResetLink(email) {
    const options = {
      method: "POST",
      body: JSON.stringify({ email }),
    }
    return await this.call("/api/accounts/send-reset-password-link/", options)
  }

  async accountsResetPassword(user_id, timestamp, signature, password) {
    const options = {
      method: "POST",
      body: JSON.stringify({ user_id, timestamp, signature, password }),
    }
    return await this.call("/api/accounts/reset-password/", options)
  }
  //#endregion

  //#region Datasets
  async datasetsRetrieve(id) {
    const data = await this.call(`/api/datasets/${id}/`)
    if (!data) {
      console.error("No dataset found with id", id)
      return null
    }
    Datasets.updateValue((datasets) => (datasets[id] = data))
    return data
  }

  async datasetsList(
    organizationId,
    limit = DEFAULT_LIMIT,
    offset = DEFAULT_OFFSET,
  ) {
    const options = {
      params: {
        limit,
        offset,
      },
    }
    if (organizationId) options.params.organization = organizationId

    const data = await this.call("/api/datasets/", options)

    if (!data) {
      throw new Response("Datasets not found", { status: 404 })
    }
    Datasets.updateValue((datasets) => {
      for (let dataset of data) {
        datasets[dataset.id] = dataset
      }
    })
    return data
  }

  async datasetsCreate(name, description, orgUrl) {
    const options = {
      method: "POST",
      body: JSON.stringify({ name, description, organization: orgUrl }),
    }
    return await this.call("/api/datasets/", options)
  }

  async datasetsDestroy(id) {
    const resp = await this.fetch(`/api/datasets/${id}/`, { method: "DELETE" })
    if (resp.status === 202) {
      return await resp.json()
    }
    return { async: false }
  }
  //#endregion

  //#region Jobs
  async jobsUpload(data, fileType) {
    if (!fileType) {
      fileType = "application/json"
    }

    const options = {
      method: "POST",
      body: data,
      headers: {
        "Content-Type": fileType,
      },
    }
    return await this.call("/api/jobs/upload/", options)
  }

  async jobsWebsocket(id) {
    const socket = new WebSocket(`${this.api}/api/ws/jobs/${id}/`)
    const throttledSetState = throttle(
      (message) => JobStatus.setValue(message),
      250,
    )

    UploadBatchStatus.setValue([])
    let lastMessageStatus = null

    socket.onopen = () => {
      console.log(`WebSocket connection opened for job ${id}`)
    }

    socket.onmessage = (event) => {
      const message = JSON.parse(event.data)
      throttledSetState(message)
      console.log(`WebSocket message for job ${id}:`, message)
      IsUploading.setValue(UploadStatus.UPLOADING)

      if (message.processed && message.total && message.status !== "failed") {
        const progress = Math.round((message.processed / message.total) * 100)
        UploadProgress.setValue(progress)
      }

      lastMessageStatus = message

      if (message.status === "complete") {
        IsUploading.setValue(UploadStatus.COMPLETED)
        UploadProgress.setValue(100)
        throttledSetState.flush()
        socket.close()
      }
    }

    socket.onerror = async (error) => {
      const errorMessage = await unrollError(error)
      console.error(`Error for job ${id}:`, errorMessage)
      addAlert(
        AlertType.ERROR,
        "WebSocket Connection",
        errorMessage || "Failed to connect to job status stream",
      )
    }

    socket.onclose = (event) => {
      console.log(`Connection closed for job ${id}:`, event)
      if (lastMessageStatus?.status === "failed") {
        IsUploading.setValue(UploadStatus.TERMINATED)
        addAlert(
          AlertType.ERROR,
          "API Error",
          "FATAL: " + lastMessageStatus?.error,
        )
      }
    }

    return socket
  }
  //#endregion

  //#region Materials
  async materialsList(offset = DEFAULT_OFFSET, limit = DEFAULT_LIMIT) {
    const options = {
      params: {
        offset,
        limit,
        proofed: false,
      },
    }
    return await this.call("/api/materials/", options)
  }

  async materialsReceive() {
    return await this.call("/api/materials/receive/", {
      method: "POST",
    })
  }

  async overridesList() {
    return await this.call("/api/materials/overrides/")
  }
  //#endregion

  //#region Identities

  /**
   * @name Api.identitiesList
   * @description Fetch the identities of the current user
   * @returns {Promise<Object>} A promise that resolves to the list of identities
   */
  async identitiesList() {
    return await this.call("/api/identities/")
  }

  /**
   * @name Api.identitiesCreate
   * @description Create a new identity
   * @returns {Promise<Object>} A promise that resolves to the new identity
   */
  async identitiesCreate() {
    return await this.fetch("/api/identities/", {
      method: "POST",
    })
  }

  //#endregion

  //#region Teams
  /**
   * @name Api.teamsList
   * @description List all teams for an organization
   * @param {string} orgId - The ID of the organization
   * @returns {Promise<Array>} A promise that resolves to an array of teams
   */
  async teamsList(orgId) {
    return await this.call(`/api/organizations/${orgId}/teams/`)
  }

  /**
   * @name Api.teamsUpdate
   * @description Update a team
   * @Warning Completely replaces the team with the new object
   * @param {string} orgId - The ID of the organization
   * @param {string} teamId - The ID of the team
   * @param {Object} body - The body of the team update
   */
  async teamsUpdate(orgId, teamId, body) {
    // Ensure the body has the required fields
    const updateBody = {
      name: body.name,
      slug: body.slug,
      organization: body.organization,
      members: Array.isArray(body.members)
        ? body.members.map((member) =>
            typeof member === "object" ? member.id : member,
          )
        : [],
    }

    return await this.call(`/api/organizations/${orgId}/teams/${teamId}/`, {
      method: "PUT",
      body: JSON.stringify(updateBody),
    })
  }

  /**
   * @name Api.teamsAddMember
   * @description Add a member to a team
   * @param {string} orgId - The ID of the organization
   * @param {string} teamId - The ID of the team
   * @param {string} memberId - The ID of the member to add
   */
  async teamsAddMember(orgId, teamId, memberId) {
    await this.fetch(
      `/api/organizations/${orgId}/teams/${teamId}/add_member/`,
      {
        method: "POST",
        body: JSON.stringify({ id: memberId }),
      },
    )
  }

  /**
   * @name Api.teamsRemoveMember
   * @description Remove a member from a team
   * @param {string} orgId - The ID of the organization
   * @param {string} teamId - The ID of the team
   * @param {string} memberId - The ID of the member to remove
   */
  async teamsRemoveMember(orgId, teamId, memberId) {
    await this.fetch(
      `/api/organizations/${orgId}/teams/${teamId}/remove_member/`,
      {
        method: "POST",
        body: JSON.stringify({ id: memberId }),
      },
    )
  }
  //#endregion

  //#region Schemas
  async schemasCreate(datasetUrl, name, description, schema) {
    const options = {
      method: "POST",
      body: JSON.stringify({ dataset: datasetUrl, name, description, schema }),
    }
    return await this.call("/api/schemas/", options)
  }

  async schemasRetrieve(id) {
    const data = await this.call(`/api/schemas/${id}/`)
    if (!data) {
      throw new Response("Schema not found", { status: 404 })
    }
    Schemas.updateValue((schemas) => (schemas[id] = data))

    if (!data.id || !data.dataset) {
      console.error("No dataset found for schema", id)
      return data
    }

    updateDatasetSchemas([data])

    return data
  }

  async schemasList(datasetId, limit = DEFAULT_LIMIT, offset = DEFAULT_OFFSET) {
    const options = {
      params: {
        limit,
        offset,
      },
    }
    if (datasetId) options.params.dataset = datasetId
    const data = await this.call("/api/schemas/", options)

    if (!data) {
      throw new Response("Schemas not found", { status: 404 })
    }

    // Update the schemas state
    Schemas.updateValue((schemas) => {
      for (let schema of data) {
        schemas[schema.id] = schema
      }
    })

    updateDatasetSchemas(data)

    return data
  }

  /**
   * @name Api.schemaTeamsList
   * @description List all teams for a schema
   * @param {string} schemaId - The ID of the schema
   * @returns {Promise<Array>} A promise that resolves to an array of teams
   */
  async schemaTeamsList(schemaId) {
    return await this.call(`/api/schemas/${schemaId}/teams/?expand=members`)
  }

  async schemasDestroy(id) {
    const resp = await this.fetch(`/api/schemas/${id}/`, { method: "DELETE" })
    if (resp.status === 202) {
      return await resp.json()
    }
    return { async: false }
  }

  async schemasDecrypt(schema) {
    if (!schema) return null

    const options = { method: "POST", body: JSON.stringify(schema) }
    const data = await this.call("/api/schemas/decrypt/", options)
    if (!data) {
      console.error("Schema decryption failed", schema)
      return null
    }
    HashedSchemas.updateValue(
      (hashedSchemas) => (hashedSchemas[schema.id] = data),
    )

    return data
  }
  //#endregion

  //#region Requests
  /**
   * @name Api.requestsList
   * @description List all outstanding requests
   * @returns {Promise<Array>} A promise that resolves to an array of requests
   */
  async requestsList(offset = DEFAULT_OFFSET, limit = DEFAULT_LIMIT) {
    const options = {
      params: {
        shared: false,
        expand: "user,schema,identity.user",
        offset,
        limit,
      },
    }
    return await this.call("/api/requests/", options)
  }

  /**
   * @name Api.requestsFulfill
   * @description Fulfill all outstanding requests
   */
  async requestsFulfill() {
    return await this.call(`/api/requests/fulfill/`, {
      method: "POST",
    })
  }

  //#endregion

  //#region Records
  async recordsList(
    schemaId,
    limit = DEFAULT_LIMIT,
    offset = DEFAULT_OFFSET,
    signal,
  ) {
    if (!schemaId) {
      throw new Response("No schema provided", { status: 400 })
    }

    const options = {
      params: {
        schema: schemaId,
        limit,
        offset,
      },
      signal,
    }
    return await this.call("/api/records/", options)
  }

  async recordsSearch(
    schemaId,
    filters,
    limit = DEFAULT_LIMIT,
    offset = DEFAULT_OFFSET,
    signal,
  ) {
    if (!schemaId) {
      throw new Response("No schema provided", { status: 400 })
    }

    const options = {
      method: "POST",
      body: JSON.stringify({
        schema: schemaId,
        filters,
        limit,
        offset,
      }),
      signal,
    }

    // Try to search; fallback to list.
    try {
      return await this.call("/api/records/search/?skip_count=true", options)
    } catch (resp) {
      if (resp.status === 405) {
        console.error("Proxy not inline; falling back to recordsList")
        return await this.recordsList(schemaId, limit, offset)
      }

      // 413 errors are already handled in call() with user-friendly messages
      if (resp.status === 413) {
        throw resp
      }

      console.error("Failed to search records", resp)
      throw resp
    }
  }

  /**
   * Fetch only the total count for a filtered query (no records returned).
   * Uses the X-Query header value from a prior recordsSearch() response
   * so the server can resolve the count without re-encrypting filters.
   */
  async recordsCount(xQuery) {
    const params = { count_only: "true" }
    for (const [key, value] of new URLSearchParams(xQuery)) {
      params[key] = value
    }
    return await this.call("/api/records/", { params })
  }

  async recordsBulkDestroy(ids) {
    if (!ids || !ids.length) return
    const params = new URLSearchParams(ids.map((id) => ["id", id]))
    await this.fetch(`/api/records/?${params.toString()}`, { method: "DELETE" })
  }

  async recordsDecrypt(records) {
    if (!records || !records.length) return []

    const options = { method: "POST", body: JSON.stringify(records) }
    return await this.call("/api/records/decrypt/", options)
  }
  //#endregion

  async deleteJobStatus(statusUrl) {
    return await this.call(statusUrl)
  }

  async recordsCanDecrypt(record) {
    const options = { method: "POST", body: JSON.stringify(record) }
    return await this.call("/api/records/can-decrypt/", options)
  }

  async usersByNameRetrieve(name) {
    return await this.call(
      `/api/users/by_name/${name}/?expand=organizations.organization.organization_user.user`,
    )
  }

  async usersSelfRetrieve() {
    // Expand the user's organizations, owner, and organization members
    return await this.call(
      `/api/users/self/?expand=organizations.organization_users.user,organizations.owner.organization_user.user`,
    )
  }

  setAuth({ email }) {
    if (email) sessionStorage.setItem(AUTH_EMAIL, email)
  }

  async currentUserRetrieve() {
    const user = CurrentUser.getValue() || {}
    const username = user?.username || this.getAuthUsername()
    const data = await this.usersSelfRetrieve()
    if (!data?.email) {
      console.error("No user found with name", username)
      return data
    }
    this.setAuth(data)
    this.setOrFindOrganization(data.organizations)
    CurrentUser.setValue(data)
    return data
  }

  //#region Organizations
  async organizationsList(limit = DEFAULT_LIMIT, offset = DEFAULT_OFFSET) {
    const options = { limit, offset }
    return await this.call("/api/organizations/", options)
  }

  async organizationsRetrieve(id) {
    return await this.call(`/api/organizations/${id}/`)
  }

  async organizationsCreate(name, stripeSessionId) {
    const payload = { name }
    if (stripeSessionId) payload.stripe_session_id = stripeSessionId
    const options = {
      method: "POST",
      body: JSON.stringify(payload),
    }
    return await this.call("/api/organizations/", options)
  }

  async organizationsUpdate(id, name, slug) {
    let payload = {}

    if (name) payload.name = name
    if (slug) payload.slug = slug

    const options = {
      method: "PUT",
      body: JSON.stringify(payload),
    }
    return await this.call(`/api/organizations/${id}/`, options)
  }

  async organizationsUsersList(
    orgId,
    limit = DEFAULT_LIMIT,
    offset = DEFAULT_OFFSET,
  ) {
    const options = { limit, offset }
    return await this.call(
      `/api/organizations/${orgId}/users/?expand=user&has_identity=true`,
      options,
    )
  }

  async organizationsUsersPartialUpdate(orgId, userId, isAdmin) {
    return await this.call(`/api/organizations/${orgId}/users/${userId}/`, {
      method: "PATCH",
      body: JSON.stringify({ is_admin: isAdmin }),
    })
  }

  async organizationsInvitationsCreate(orgId, email, isAdmin) {
    return await this.call(`/api/organizations/${orgId}/invitations/`, {
      method: "POST",
      body: JSON.stringify({ email, is_admin: isAdmin }),
    })
  }
  //#endregion
  async statusRetrieve() {
    return await this.call("/api/status/")
  }

  /**
   * Retrieve a Stripe checkout session by ID.
   * @param {string} sessionId Stripe checkout session ID
   */
  async stripeGetCheckoutSession(sessionId) {
    return await this.call(
      `/api/stripe/checkout-session/${encodeURIComponent(sessionId)}/`,
      { method: "GET" },
    )
  }

  /**
   * Stripe customer for the organization. Caller must be an org admin.
   * @param {string} [organizationId] defaults to session org
   */
  async stripeCreateCustomer(organizationId) {
    const org =
      organizationId ||
      (typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem(AUTH_ORGANIZATION_ID)
        : "") ||
      ""
    if (!org) {
      throw new Response(
        JSON.stringify({ detail: "organization is required" }),
        { status: 400 },
      )
    }
    return await this.call("/api/stripe/customer/", {
      method: "POST",
      body: JSON.stringify({ organization: org }),
    })
  }

  /**
   * @param {string} [organizationId] defaults to session org
   * @returns {Promise<{ active: boolean }>}
   */
  async stripeSubscriptionStatus(organizationId) {
    const org =
      organizationId ||
      (typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem(AUTH_ORGANIZATION_ID)
        : "") ||
      ""
    if (!org) {
      throw new Response(
        JSON.stringify({ detail: "organization is required" }),
        { status: 400 },
      )
    }
    return await this.call(
      `/api/stripe/subscription-status/?organization=${encodeURIComponent(
        org,
      )}`,
      { method: "GET" },
    )
  }
}
